import { useGA4Core } from '@/composables/common/analytics/useGA4Core';

/**
 * useEventoTracking
 * Domain-specific facade for Evento (Event/Ticket) purchase tracking in GA4.
 *
 * This composable handles the mapping of evento-specific data structures to GA4 standard DTOs.
 * It delegates all GA4 tracking calls to useGA4Core for validation and dataLayer management.
 *
 * Usage:
 *   const { trackViewItemList, trackViewItem, trackBeginCheckout, trackAddPaymentInfo, trackPurchase } = useEventoTracking({ store, getVuexAlias });
 *   await trackPurchase(eventoResumen, tpvOrder, sessionId);
 */

/**
 * Maps individual evento entrada (ticket) and producto items to GA4 format
 * @param {Object} eventoResumen - The evento purchase summary
 * @returns {Array} Array of GA4-formatted items
 */
const mapEventoItems = eventoResumen => {
    const items = [];

    // Add one item per entrada (ticket from entradas array)
    // Entradas may come as `entradas` array OR legacy `datos_compradores` array
    const entradas = eventoResumen.entradas || eventoResumen.datos_compradores || [];

    if (Array.isArray(entradas) && entradas.length > 0) {
        // Group by price to aggregate quantities for each price point
        const uniquePrices = {};
        entradas.forEach((entrada, idx) => {
            // Default to 1 if unidades not provided (for completed purchases)
            const unidades = entrada.unidades || 1;

            // Only process entradas with actual quantity selected
            if (unidades > 0) {
                // CRITICAL: Convert price to number (may come as string or nested object from SGA API)
                // Fallback order: precio.amount (SGA schema) → price → precio → importe_con_descuento → importe → 0
                const price = parseFloat(entrada.precio?.amount || entrada.price || entrada.precio || entrada.importe_con_descuento || entrada.importe || 0);

                if (!uniquePrices[price]) {
                    uniquePrices[price] = {
                        price: price,
                        quantity: 0,
                        name: entrada.name || entrada.nombre || 'Entrada',
                    };
                }
                uniquePrices[price].quantity += unidades;
            }
        });

        // Create items from grouped prices
        Object.values(uniquePrices).forEach(priceGroup => {
            if (priceGroup.quantity > 0) {
                items.push({
                    item_id: String(eventoResumen.evento_id || ''),
                    item_name: eventoResumen.evento_name || 'Evento',
                    price: priceGroup.price,  // Now a number
                    quantity: priceGroup.quantity,
                    item_category: 'Evento',
                });
            }
        });
    }

    // Add productos if present
    if (Array.isArray(eventoResumen.productos) && eventoResumen.productos.length > 0) {
        eventoResumen.productos.forEach(p => {
            items.push({
                item_id: String(p.id || p.teatro_product_id || p.product_id || ''),
                item_name: p.nombre || p.name || 'Producto',
                price: parseFloat(p.price || p.precio || 0),  // Convert to number
                quantity: p.unidades || p.units || 1,
                item_category: 'Producto',
            });
        });
    }

    return items;
};

export function useEventoTracking(deps = {}) {
    // Normalize and validate dependencies
    const { store, getVuexAlias } = deps;
    
    if (!store) {
        console.warn('[EventoTracking] useEventoTracking called without store dependency. GA4 tracking will not function.');
    }
    
    const ga4Core = useGA4Core(store, getVuexAlias);

    /**
     * trackViewItemList(eventosList)
     * Track list view event for all events in a list
     * @param {Array} eventosList - Array of evento objects
     * @returns {Boolean} Success indicator
     */
    const trackViewItemList = eventosList => {
        try {
            if (!Array.isArray(eventosList) || eventosList.length === 0) {
                console.warn('[EventoTracking] trackViewItemList - No events provided');
                return false;
            }

            const items = [];
            let totalValue = 0;

            // Build items from all eventos in the list
            eventosList.forEach(evento => {
                if (evento.evento_id && evento.evento_name) {
                    const item = {
                        item_id: String(evento.evento_id),
                        item_name: evento.evento_name,
                        price: parseFloat(evento.price || evento.precio || 0),
                        quantity: 1,
                        item_category: 'Evento',
                    };
                    items.push(item);
                    totalValue += item.price;
                }
            });

            if (items.length === 0) {
                console.warn('[EventoTracking] trackViewItemList - No valid items to track');
                return false;
            }

            const dto = {
                value: totalValue,
                currency: 'EUR',
                items,
            };

            return ga4Core.trackViewItemList(dto);
        } catch (error) {
            console.error('[EventoTracking] trackViewItemList - Error:', error);
            return false;
        }
    };

    /**
     * trackViewItem(evento)
     * Track single event detail page view
     * @param {Object} evento - Single evento object
     * @returns {Boolean} Success indicator
     */
    const trackViewItem = evento => {
        try {
            if (!evento || !evento.evento_id || !evento.evento_name) {
                console.warn('[EventoTracking] trackViewItem - Invalid evento data');
                return false;
            }

            const price = parseFloat(evento.price || evento.precio || 0);
            const item = {
                item_id: String(evento.evento_id),
                item_name: evento.evento_name,
                price,
                quantity: 1,
                item_category: 'Evento',
            };

            const dto = {
                value: price,
                currency: 'EUR',
                items: [item],
            };

            return ga4Core.trackViewItem(dto);
        } catch (error) {
            console.error('[EventoTracking] trackViewItem - Error:', error);
            return false;
        }
    };

    /**
     * trackBeginCheckout(eventoResumen)
     * Track checkout flow start after seats/entries are locked
     * @param {Object} eventoResumen - Evento purchase summary with datos_compradores, productos, etc.
     * @returns {Boolean} Success indicator
     */
    const trackBeginCheckout = eventoResumen => {
        try {
            if (!eventoResumen || !eventoResumen.evento_id || !eventoResumen.evento_name) {
                console.warn('[EventoTracking] trackBeginCheckout - Invalid eventoResumen');
                return false;
            }

            const items = mapEventoItems(eventoResumen);

            if (items.length === 0) {
                console.warn('[EventoTracking] trackBeginCheckout - No items mapped from eventoResumen');
                return false;
            }

            const dto = {
                value: eventoResumen.total_price || 0,
                currency: 'EUR',
                items,
            };

            return ga4Core.trackBeginCheckout(dto);
        } catch (error) {
            console.error('[EventoTracking] trackBeginCheckout - Error:', error);
            return false;
        }
    };

    /**
     * trackAddPaymentInfo(eventoResumen, paymentMethod)
     * Track payment method selection during checkout
     * @param {Object} eventoResumen - Evento purchase summary
     * @param {String} paymentMethod - Selected payment method (optional)
     * @returns {Boolean} Success indicator
     */
    const trackAddPaymentInfo = (eventoResumen, paymentMethod = '') => {
        try {
            if (!eventoResumen || !eventoResumen.evento_id || !eventoResumen.evento_name) {
                console.warn('[EventoTracking] trackAddPaymentInfo - Invalid eventoResumen');
                return false;
            }

            const items = mapEventoItems(eventoResumen);

            if (items.length === 0) {
                console.warn('[EventoTracking] trackAddPaymentInfo - No items mapped from eventoResumen');
                return false;
            }

            const dto = {
                value: eventoResumen.total_price || 0,
                currency: 'EUR',
                items,
            };

            return ga4Core.trackAddPaymentInfo(dto, paymentMethod);
        } catch (error) {
            console.error('[EventoTracking] trackAddPaymentInfo - Error:', error);
            return false;
        }
    };

    /**
     * trackPurchase(eventoResumen, tpvOrder, sessionId) - ASYNC
     * Track completed purchase event for evento
     * Delegates to ga4Core.trackPurchase with tipo='eventos'
     * @param {Object} eventoResumen - Evento purchase summary with total_price, session_id, etc.
     * @param {String} tpvOrder - TPV order ID from payment processor
     * @param {String} sessionId - Session ID for user tracking
     * @returns {Promise<Boolean>} Success indicator
     */
    const trackPurchase = async(eventoResumen, tpvOrder, sessionId) => {
        try {
            if (!eventoResumen || !eventoResumen.evento_id || !eventoResumen.evento_name) {
                console.warn('[EventoTracking] trackPurchase - Invalid eventoResumen');
                return false;
            }

            if (!tpvOrder || typeof tpvOrder !== 'string') {
                console.warn('[EventoTracking] trackPurchase - Invalid tpvOrder');
                return false;
            }

            if (!sessionId || typeof sessionId !== 'string') {
                console.warn('[EventoTracking] trackPurchase - Invalid sessionId');
                return false;
            }

            const items = mapEventoItems(eventoResumen);

            if (items.length === 0) {
                console.warn('[EventoTracking] trackPurchase - No items mapped from eventoResumen');
                return false;
            }

            const transactionId = tpvOrder;
            const dto = {
                value: eventoResumen.total_price || 0,
                currency: 'EUR',
                items,
                transactionId,
            };

            return await ga4Core.trackPurchase(dto, tpvOrder, sessionId, 'entradas');
        } catch (error) {
            console.error('[EventoTracking] trackPurchase - Error:', error);
            return false;
        }
    };

    return {
        trackViewItemList,
        trackViewItem,
        trackBeginCheckout,
        trackAddPaymentInfo,
        trackPurchase,
    };
}
